import { useEffect, useState } from 'react';
import { getGraphClient } from '../utils/graphService';
import { directSupabase } from '../lib/supabaseClient';

export interface OutlookCategory {
  id: string;
  displayName: string;
  color: string;
}

export interface OutlookEvent {
  id: string;
  subject: string;
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  location?: { displayName: string };
  attendees?: Array<{ emailAddress: { address: string; name: string }; type: string }>;
  bodyPreview?: string;
  isAllDay?: boolean;
  categories: string[];
}

export interface DatabaseEvent {
  id: string;
  project_id: string;
  event_type: string;
  event_date: string;
  calendar_event_id: string | null;
  title: string | null;
  project?: {
    project_name: string;
    customer_name: string;
    customer_email: string;
  };
}

// Module-level singleton promises
let masterCategoriesPromise: Promise<OutlookCategory[]> | null = null;
const calendarIdPromises: Record<string, Promise<string>> = {};
const eventCache: Record<string, { data: OutlookEvent; timestamp: number }> = {};
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes - much longer cache duration
const dbEventsCache: Record<string, { data: any[]; timestamp: number }> = {};
const DB_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes for database events

// Function to clear all caches
export function clearAllCaches(): void {
  // Clear event cache
  Object.keys(eventCache).forEach(key => delete eventCache[key]);
  
  // Clear database events cache
  Object.keys(dbEventsCache).forEach(key => delete dbEventsCache[key]);
  
  // Clear calendar ID promises
  Object.keys(calendarIdPromises).forEach(key => delete calendarIdPromises[key]);
  
  // Clear master categories promise
  masterCategoriesPromise = null;
  
  // Clear session storage
  try {
    sessionStorage.removeItem('outlookCalendarId:Calendar');
    sessionStorage.removeItem('outlookCategories');  // Clear category cache
  } catch (e) {
    // Ignore errors
  }
  
  console.log('All caches cleared');
}

// Function to clear cache for a specific event
export function clearEventCache(eventId: string): void {
  if (eventCache[eventId]) {
    delete eventCache[eventId];
    console.log(`Cache cleared for event: ${eventId}`);
  }
}

function getCached<T>(key: string): T | null {
  try {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function setCached<T>(key: string, value: T) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function fetchMasterCategoriesOnce(client: any): Promise<OutlookCategory[]> {
  if (masterCategoriesPromise) return masterCategoriesPromise;
  const cacheKey = 'outlookCategories';
  const cached = getCached<OutlookCategory[]>(cacheKey) || [];
  if (cached.length > 0) {
    masterCategoriesPromise = Promise.resolve(cached);
    return masterCategoriesPromise;
  }
  masterCategoriesPromise = client.api('/me/outlook/masterCategories').get().then((response: any) => {
    setCached(cacheKey, response.value || []);
    return response.value || [];
  });
  return masterCategoriesPromise || Promise.resolve([]);
}

async function fetchCalendarIdOnce(client: any, calendarName: string): Promise<string> {
  if (Object.prototype.hasOwnProperty.call(calendarIdPromises, calendarName)) return calendarIdPromises[calendarName];
  const cacheKey = `outlookCalendarId:${calendarName}`;
  const cached = getCached<string>(cacheKey);
  if (cached) {
    calendarIdPromises[calendarName] = Promise.resolve(cached);
    return cached;
  }
  calendarIdPromises[calendarName] = client.api('/me/calendars').get().then((calendarsResponse: any) => {
    const calendar = calendarsResponse.value.find((cal: any) => cal.name === calendarName);
    if (!calendar) throw new Error(`Calendar '${calendarName}' not found.`);
    setCached(cacheKey, calendar.id);
    return calendar.id;
  });
  return calendarIdPromises[calendarName];
}

async function fetchDatabaseEvents(startDate: Date, endDate: Date): Promise<any[]> {
  // Create cache key based on date range
  const cacheKey = `${startDate.toISOString()}_${endDate.toISOString()}`;
  const now = Date.now();
  
  // Check cache first
  const cached = dbEventsCache[cacheKey];
  if (cached && (now - cached.timestamp) < DB_CACHE_DURATION) {
    return cached.data;
  }
  
  const { data, error } = await directSupabase
    .from('project_events')
    .select(`
      id,
      event_type,
      title,
      event_date,
      calendar_event_id,
      metadata,
      project:projects(
        id,
        project_name,
        customer_name
      )
    `)
    .not('calendar_event_id', 'is', null)
    .gte('event_date', startDate.toISOString())
    .lte('event_date', endDate.toISOString())
    .order('event_date', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch database events: ${error.message}`);
  }

  const result = data || [];
  
  // Cache the result
  dbEventsCache[cacheKey] = { data: result, timestamp: now };
  
  return result;
}

async function fetchOutlookEvents(client: any, calendarId: string, eventIds: string[]): Promise<OutlookEvent[]> {
  if (eventIds.length === 0) {
    return [];
  }

  try {
    // Check cache first
    const now = Date.now();
    const cachedEvents: OutlookEvent[] = [];
    const uncachedIds: string[] = [];
    // Dedupe incoming event IDs to avoid redundant requests
    const uniqueEventIds = Array.from(new Set(eventIds));
    
    uniqueEventIds.forEach(id => {
      const cached = eventCache[id];
      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        cachedEvents.push(cached.data);
      } else {
        uncachedIds.push(id);
      }
    });
    
    // If all events are cached, return them
    if (uncachedIds.length === 0) {
      return cachedEvents;
    }
    
    // Fetch uncached events using Microsoft Graph $batch (max 20 requests per batch)
    const BATCH_LIMIT = 20;
    const newEvents: OutlookEvent[] = [];
    for (let i = 0; i < uncachedIds.length; i += BATCH_LIMIT) {
      const batchIds = uncachedIds.slice(i, i + BATCH_LIMIT);
      const batchRequests = batchIds.map((id, idx) => ({
        id: String(idx + 1),
        method: 'GET',
        url: `/me/calendars/${calendarId}/events/${id}?$select=id,subject,start,end,location,attendees,bodyPreview,isAllDay,categories`
      }));
      const batchResponse = await client.api('/$batch').post({ requests: batchRequests });
      const batchEvents = (batchResponse?.responses || [])
        .filter((r: any) => r.status === 200 && r.body)
        .map((r: any) => r.body) as OutlookEvent[];
      newEvents.push(...batchEvents);
    }
    
    // Cache the new events
    newEvents.forEach(event => {
      eventCache[event.id] = { data: event, timestamp: now };
    });
    
    // Return both cached and new events
    return [...cachedEvents, ...newEvents];
  } catch (error: any) {
    console.error('Error fetching Outlook events:', error);
    return [];
  }
}

export function useOutlookCalendar(calendarName: string, startDate: Date, endDate: Date) {
  const [categories, setCategories] = useState<OutlookCategory[]>([]);
  const [events, setEvents] = useState<OutlookEvent[]>([]);
  const [eventMetadata, setEventMetadata] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    
    // Set a timeout to show loading state immediately
    const loadingTimeout = setTimeout(() => {
      if (isMounted) setLoading(true);
    }, 0);

    (async () => {
      try {
        // Start all async operations in parallel
        const [client, dbEvents] = await Promise.all([
          getGraphClient(),
          fetchDatabaseEvents(startDate, endDate)
        ]);
        
        if (!isMounted) return;
        
        // Extract Outlook event IDs from database events and build metadata map
        const outlookEventIds = dbEvents
          .map(event => event.calendar_event_id)
          .filter((id): id is string => id !== null);
        
        // Build metadata map: calendar_event_id -> metadata
        const metadataMap: Record<string, any> = {};
        dbEvents.forEach(event => {
          if (event.calendar_event_id && event.metadata) {
            metadataMap[event.calendar_event_id] = event.metadata;
          }
        });
        
        if (outlookEventIds.length === 0) {
          if (isMounted) {
            setEvents([]);
            setEventMetadata({});
            setLoading(false);
          }
          return;
        }
        
        // Start fetching categories and calendar ID in parallel
        const [cats, calendarId] = await Promise.all([
          fetchMasterCategoriesOnce(client),
          fetchCalendarIdOnce(client, calendarName)
        ]);
        
        if (!isMounted) return;
        
        // Set categories immediately
        setCategories(cats);
        
        // Fetch Outlook events
        const outlookEvents = await fetchOutlookEvents(client, calendarId, outlookEventIds);
        
        if (isMounted) {
          setEvents(outlookEvents);
          setEventMetadata(metadataMap);
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Error in useOutlookCalendar:', err);
        if (isMounted) {
          setError(err.message || 'Failed to load Outlook calendar data');
          setLoading(false);
        }
      }
    })();
    
    return () => { 
      isMounted = false;
      clearTimeout(loadingTimeout);
    };
  }, [calendarName, startDate, endDate, refreshTrigger]);

  return { categories, events, eventMetadata, loading, error, refresh };
}

// Function to clean up orphaned project events (those with deleted calendar events)
export async function cleanupOrphanedEvents(): Promise<void> {
  try {
    // Get all project events with calendar_event_id
    const { data: projectEvents, error } = await directSupabase
      .from('project_events')
      .select('id, calendar_event_id')
      .not('calendar_event_id', 'is', null);

    if (error) {
      console.error('Error fetching project events for cleanup:', error);
      return;
    }

    if (!projectEvents || projectEvents.length === 0) {
      return;
    }

    // Get Graph client
    const client = await getGraphClient();
    const calendarId = await fetchCalendarIdOnce(client, 'Calendar');

    // Check which calendar events still exist
    const calendarEventIds = projectEvents.map(pe => pe.calendar_event_id);
    const existingEventIds: string[] = [];

    // Check events in batches
    const batchSize = 10;
    for (let i = 0; i < calendarEventIds.length; i += batchSize) {
      const batch = calendarEventIds.slice(i, i + batchSize);
      const batchPromises = batch.map(async (eventId) => {
        try {
          await client.api(`/me/calendars/${calendarId}/events/${eventId}`).get();
          return eventId;
        } catch (error: any) {
          if (error?.message?.includes('not found in the store')) {
            return null; // Event was deleted
          }
          return eventId; // Keep it if it's a different error
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      existingEventIds.push(...batchResults.filter(Boolean));
    }

    // Find orphaned events (those with calendar_event_id that no longer exists)
    const orphanedEvents = projectEvents.filter(pe => 
      !existingEventIds.includes(pe.calendar_event_id!)
    );

    if (orphanedEvents.length > 0) {
      console.log(`Found ${orphanedEvents.length} orphaned events, cleaning up...`);
      
      // Remove calendar_event_id from orphaned events (don't delete the project event)
      const { error: updateError } = await directSupabase
        .from('project_events')
        .update({ calendar_event_id: null })
        .in('id', orphanedEvents.map(e => e.id));

      if (updateError) {
        console.error('Error cleaning up orphaned events:', updateError);
      } else {
        console.log(`Successfully cleaned up ${orphanedEvents.length} orphaned events`);
      }
    }
  } catch (error) {
    console.error('Error in cleanupOrphanedEvents:', error);
  }
} 